import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { KPICard, TimeSeriesChart, BreakdownChart } from '@/components/analytics';
import { ErrorState } from '@/components/data-display';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ActivityPanel from '@/components/analytics/ActivityPanel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Scan,
  Users,
  TrendingUp,
  Globe,
  Calendar,
  MapPin,
  Building2,
  Handshake,
  Home,
  Filter,
  X,
  BarChart3,
  QrCode,
  ArrowLeft,
} from 'lucide-react';
import { apiV1 } from '@/api/client';
import { analyticsApi } from '@/api/analytics';

export default function Analytics() {
  // When this page is opened at /analytics/:qrId, all queries below are
  // scoped to that single QR code and the tracking-parameter filter card is
  // hidden. Otherwise the filter card applies to every tab: Activity, QR
  // Overview and By Tracking all send the same filters (as snake_case
  // region_id/office_id/... via analyticsApi), plus the date range.
  const { qrId } = useParams();

  const [dateRange, setDateRange] = useState('30d');
  const [granularity, setGranularity] = useState('day');
  // Activity is the global default: it's the only view spanning every link
  // type. But a QR-scoped deep link (/analytics/:qrId) must land on the QR
  // view — Activity ignores qrId, so defaulting there would silently show
  // global numbers to someone who asked about one QR code.
  const [activeTab, setActiveTab] = useState(qrId ? 'overview' : 'activity');

  // The scoped QR's details (just enough for the header banner)
  const [scopedQr, setScopedQr] = useState(null);

  // Tracking filters
  const [filters, setFilters] = useState({
    regionId: '',
    officeId: '',
    agentId: '',
    partnershipId: '',
    listingRef: '',
  });
  
  // Reference data for filters
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: [],
  });
  const [loadingRef, setLoadingRef] = useState(true);
  
  // Smart filtering state
  const [officeTypeFilter, setOfficeTypeFilter] = useState('all');
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  
  // Grouped analytics data
  const [groupedData, setGroupedData] = useState(null);
  const [loadingGrouped, setLoadingGrouped] = useState(false);
  const [selectedGroupBy, setSelectedGroupBy] = useState('region');

  const {
    overview,
    timeSeries,
    deviceBreakdown,
    countryBreakdown,
    funnel,
    loading,
    error,
    refetch,
  } = useAnalytics({
    period: dateRange,
    granularity,
    // Every analytics endpoint accepts qr_code_id and the tracking filters;
    // analyticsApi turns regionId etc. into the region_id the API reads.
    ...(qrId ? { qr_code_id: qrId } : filters),
  });

  // Fetch reference data on mount
  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Fetch the scoped QR's name/short_code for the header banner
  useEffect(() => {
    if (!qrId) {
      setScopedQr(null);
      return;
    }
    let cancelled = false;
    apiV1.get(`/qrcodes/${qrId}`)
      .then((res) => { if (!cancelled && res.data?.success) setScopedQr(res.data.data); })
      .catch(() => { /* leave scopedQr null; the banner falls back to the id */ });
    return () => { cancelled = true; };
  }, [qrId]);

  // Fetch grouped analytics when group selection changes (or when
  // switching between global / QR-scoped via the URL param).
  useEffect(() => {
    if (activeTab === 'breakdown') {
      fetchGroupedAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedGroupBy, dateRange, qrId, JSON.stringify(filters)]);

  const fetchReferenceData = async () => {
    setLoadingRef(true);
    try {
      const response = await apiV1.get('/reference-data/options');
      if (response.data.success) {
        const data = {
          regions: response.data.data.region || [],
          offices: response.data.data.office || [],
          agents: response.data.data.agent || [],
          partnerships: response.data.data.partnership || [],
        };
        setReferenceData(data);
        setFilteredOffices(data.offices);
        setFilteredAgents(data.agents);
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
    } finally {
      setLoadingRef(false);
    }
  };

  // Smart filtering: Update filtered offices when region or office type changes
  useEffect(() => {
    let filtered = referenceData.offices;
    
    // Filter by region
    if (filters.regionId) {
      filtered = filtered.filter(o => o.region_id === filters.regionId);
    }
    
    // Filter by office type
    if (officeTypeFilter !== 'all') {
      filtered = filtered.filter(o => o.office_type === officeTypeFilter);
    }
    
    setFilteredOffices(filtered);
    
    // Reset office selection if it's no longer in the filtered list
    if (filters.officeId) {
      const stillValid = filtered.some(o => o.id === filters.officeId);
      if (!stillValid) {
        setFilters(prev => ({ ...prev, officeId: '', agentId: '' }));
      }
    }
  }, [filters.regionId, officeTypeFilter, referenceData.offices]);

  // Smart filtering: Update filtered agents when office or region changes
  useEffect(() => {
    let filtered = referenceData.agents;
    
    if (filters.officeId) {
      // Show only agents for the selected office
      filtered = filtered.filter(a => a.office_id === filters.officeId);
    } else if (filters.regionId) {
      // If no office selected but region is, show agents from offices in that region
      const regionOfficeIds = referenceData.offices
        .filter(o => o.region_id === filters.regionId)
        .map(o => o.id);
      filtered = filtered.filter(a => regionOfficeIds.includes(a.office_id));
    }
    
    setFilteredAgents(filtered);
    
    // Reset agent selection if it's no longer in the filtered list
    if (filters.agentId) {
      const stillValid = filtered.some(a => a.id === filters.agentId);
      if (!stillValid) {
        setFilters(prev => ({ ...prev, agentId: '' }));
      }
    }
  }, [filters.officeId, filters.regionId, referenceData.agents, referenceData.offices]);

  const fetchGroupedAnalytics = async () => {
    setLoadingGrouped(true);
    try {
      const response = { data: await analyticsApi.getGrouped(selectedGroupBy, {
        period: dateRange,
        limit: 10,
        ...(qrId ? { qr_code_id: qrId } : filters),
      }) };

      if (response.data.success) {
        setGroupedData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load grouped analytics:', error);
    } finally {
      setLoadingGrouped(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      regionId: '',
      officeId: '',
      agentId: '',
      partnershipId: '',
      listingRef: '',
    });
    setOfficeTypeFilter('all');
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Analytics" description="Scan analytics and insights" />
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <PageHeader
        title={qrId ? `Analytics: ${scopedQr?.name ?? '…'}` : 'Analytics'}
        description={
          qrId
            ? 'Scan activity for this QR code only'
            : 'Monitor scan activity and campaign performance'
        }
      />

      {qrId && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <QrCode className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm font-medium">
                  Filtered to QR code: {scopedQr?.name ?? qrId}
                </p>
                {scopedQr?.short_code && (
                  <p className="text-xs text-muted-foreground">
                    Short code: <code className="bg-muted px-1 rounded">{scopedQr.short_code}</code>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {scopedQr && (
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/qrs/${qrId}`}>Open QR detail</Link>
                </Button>
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to="/analytics">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  All analytics
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Date & Granularity Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-[160px]" data-testid="analytics-range-picker">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
        <Select value={granularity} onValueChange={setGranularity}>
          <SelectTrigger className="w-[140px]" data-testid="analytics-granularity">
            <SelectValue placeholder="Granularity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hour">Hourly</SelectItem>
            <SelectItem value="day">Daily</SelectItem>
            <SelectItem value="week">Weekly</SelectItem>
            <SelectItem value="month">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tracking Parameter Filters - hidden in QR-scoped view */}
      {!qrId && (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base font-medium">Filter by Tracking Parameters</CardTitle>
              {activeFilterCount > 0 && (
                <Badge variant="secondary">{activeFilterCount} active</Badge>
              )}
            </div>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear all
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Region Filter */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Region
              </label>
              <Select
                value={filters.regionId || "all"}
                onValueChange={(v) => updateFilter('regionId', v === "all" ? '' : v)}
                disabled={loadingRef}
              >
                <SelectTrigger className="h-9" data-testid="filter-region">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {referenceData.regions.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Office Type Filter */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Office Type
              </label>
              <Select
                value={officeTypeFilter}
                onValueChange={setOfficeTypeFilter}
                disabled={loadingRef}
              >
                <SelectTrigger className="h-9" data-testid="filter-office-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                  <SelectItem value="franchise">Franchise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Office Filter - Smart Filtered */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Office
                {filteredOffices.length > 0 && (
                  <span className="text-xs">({filteredOffices.length})</span>
                )}
              </label>
              <Select
                value={filters.officeId || "all"}
                onValueChange={(v) => updateFilter('officeId', v === "all" ? '' : v)}
                disabled={loadingRef}
              >
                <SelectTrigger className="h-9" data-testid="filter-office">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Offices</SelectItem>
                  {filteredOffices.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                      {o.office_type && <span className="ml-1 text-muted-foreground">({o.office_type})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Agent Filter - Smart Filtered */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Users className="h-3 w-3" /> Agent
                {filteredAgents.length > 0 && (
                  <span className="text-xs">({filteredAgents.length})</span>
                )}
              </label>
              <Select
                value={filters.agentId || "all"}
                onValueChange={(v) => updateFilter('agentId', v === "all" ? '' : v)}
                disabled={loadingRef}
              >
                <SelectTrigger className="h-9" data-testid="filter-agent">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents</SelectItem>
                  {filteredAgents.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Partnership Filter */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Handshake className="h-3 w-3" /> Partnership
              </label>
              <Select
                value={filters.partnershipId || "all"}
                onValueChange={(v) => updateFilter('partnershipId', v === "all" ? '' : v)}
                disabled={loadingRef}
              >
                <SelectTrigger className="h-9" data-testid="filter-partnership">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partnerships</SelectItem>
                  {referenceData.partnerships.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Listing Ref Filter */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground flex items-center gap-1">
                <Home className="h-3 w-3" /> Listing
              </label>
              <Input
                className="h-9"
                placeholder="Listing ref"
                value={filters.listingRef}
                onChange={(e) => updateFilter('listingRef', e.target.value.trim())}
                data-testid="filter-listing"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="overview">QR Overview</TabsTrigger>
          <TabsTrigger value="breakdown">By Tracking</TabsTrigger>
        </TabsList>

        {/* Activity spans every link type. QR Overview and By Tracking read
            QR scans only. All three follow the date range and the filters. */}
        <TabsContent value="activity" className="mt-4">
          <ActivityPanel
            period={dateRange}
            granularity={granularity}
            filters={qrId ? { qr_code_id: qrId } : filters}
          />
        </TabsContent>

        <TabsContent value="overview" className="space-y-6 mt-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Scans"
              value={formatNumber(overview?.total_scans)}
              change={overview?.scans_change_percent ?? undefined}
              icon={Scan}
              loading={loading}
            />
            <KPICard
              title="Unique Scans"
              value={formatNumber(overview?.unique_scans)}
              change={overview?.unique_change_percent ?? undefined}
              icon={Users}
              loading={loading}
            />
            <KPICard
              title="Unique Rate"
              value={`${(overview?.unique_rate || 0).toFixed(1)}%`}
              changeLabel="unique scanners / scans"
              icon={TrendingUp}
              loading={loading}
            />
            <KPICard
              title="Top Country"
              value={overview?.top_country || 'N/A'}
              changeLabel={overview?.top_country_percentage ? `${overview.top_country_percentage.toFixed(1)}%` : undefined}
              icon={Globe}
              loading={loading}
            />
          </div>

          {/* Time Series Chart */}
          <TimeSeriesChart
            data={timeSeries}
            title="Scans Over Time"
            description="QR scans per South African day (or hour/week/month). Bot traffic excluded."
            uniqueLabel="Unique scanners"
            loading={loading}
            showUnique={true}
          />

          {/* Breakdown Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BreakdownChart
              data={deviceBreakdown}
              title="Device Breakdown"
              loading={loading}
            />
            <BreakdownChart
              data={countryBreakdown}
              title="Geographic Distribution"
              loading={loading}
            />
          </div>

          {/* Funnel: each stage counted from its own collection, same window
              and filters (page views from page_views, bots excluded). */}
          <Card data-testid="funnel-card">
            <CardHeader>
              <CardTitle className="text-base font-medium">Funnel</CardTitle>
              <CardDescription>
                Each stage is counted separately over the same period; the percentage is of the stage above.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(funnel?.stages || []).map((stage, i) => (
                  <div className="space-y-2" key={stage.key} data-testid={`funnel-${stage.key}`}>
                    <div className="flex justify-between text-sm">
                      <span>{stage.label}</span>
                      <span className="font-medium">
                        {formatNumber(stage.count)}
                        {i > 0 ? ` (${(stage.percentage || 0).toFixed(1)}%)` : ''}
                      </span>
                    </div>
                    <Progress
                      value={i === 0 ? (stage.count ? 100 : 0) : Math.min(stage.percentage || 0, 100)}
                      className="h-3"
                    />
                  </div>
                ))}
                {!funnel && !loading && (
                  <p className="text-sm text-muted-foreground">No funnel data.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="breakdown" className="space-y-6 mt-4">
          {/* Group By Selector */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Group by:</span>
            <Select value={selectedGroupBy} onValueChange={setSelectedGroupBy}>
              <SelectTrigger className="w-[180px]">
                <BarChart3 className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="region">Region</SelectItem>
                <SelectItem value="branch">Branch</SelectItem>
                <SelectItem value="office">Office</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="listing_ref">Listing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Grouped Analytics Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium capitalize">
                Performance by {selectedGroupBy.replace('_', ' ')}
              </CardTitle>
              <CardDescription>
                Top 10 {selectedGroupBy.replace('_', ' ')}s by QR scan volume, using the linkage stamped on each scan
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingGrouped ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : groupedData?.groups?.length > 0 ? (
                <div className="space-y-3">
                  {groupedData.groups.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.qr_codes_count} QR codes
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatNumber(item.total_scans)}</p>
                        <p className="text-xs text-muted-foreground">scans</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatNumber(item.unique_scans)}</p>
                        <p className="text-xs text-muted-foreground">unique</p>
                      </div>
                      <div className="w-24">
                        <Progress value={item.percentage_of_total} className="h-2" />
                        <p className="text-xs text-muted-foreground text-right mt-1">
                          {item.percentage_of_total}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No data available for the selected criteria
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {groupedData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Total Groups</p>
                  <p className="text-2xl font-bold">{groupedData.total_groups}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">Total Scans</p>
                  <p className="text-2xl font-bold">{formatNumber(groupedData.grand_total_scans)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">
                    Not linked to a {selectedGroupBy.replace('_', ' ')}
                  </p>
                  <p className="text-2xl font-bold" data-testid="grouped-unassigned">
                    {formatNumber(groupedData.unassigned_scans)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
